// Cargo block of the printed bill of lading (HBL, MBL and DBL).
//
// One line per container, spread over the whole cargo box: container no, seal, type and the
// packages on the left, the goods under "Description of goods", the weight under "Gross
// weight" and the volume under "Measurement":
//
//   MRKU3673550  ML-TR1678899  40'HC  24 PACKAGES   BETUME          27160.000 KGS   86.0000 CBM
//
// A container that holds several cargo lines (the same container number on several rows) is
// printed as a block, its number / type / seal stacked on the left, one line per cargo line:
//
//   GAOU7947044  241 PCS        INNER FENDER- GTIP:870829           657.680 KGS   12.5500 CBM
//   40HQ          15 PCS        BUMPER GRILL- GTIP:870810
//   MLTR1541414  125 PCS        BUMPER- GTIP:870810
//                381 PCS        TOTAL OF CONTAINER
//
// The TOTAL lines close the block, one per container type and one per package kind:
//
//   TOTAL         20X40'HC   500 PALLETS                     540000.000 KGS 1720.0000 CBM
//                  5X20'DC   100 BOXES
//
// Set in a monospaced font so the columns line up. Lines that do not fit in the cargo box
// continue on attached sheets.
import { rgb, StandardFonts } from "pdf-lib";

const toNumber = (value) => Number(value) || 0;
const clean = (value) => String(value ?? "").replace(/\s+/g, " ").trim();
const kgs = (value) => `${toNumber(value).toFixed(3)} KGS`;
const cbms = (value) => `${toNumber(value).toFixed(4)} CBM`;
const containerKey = (value) => clean(value).replace(/[^A-Z0-9]/gi, "").toUpperCase();

const COLUMN_GAP = "  ";

// Pads each column to its widest value; empty columns are dropped.
// Columns listed in rightAligned (numbers) are padded on the left.
const alignColumns = (rows, rightAligned = []) => {
    const widths = rows[0]?.map((_, column) => Math.max(...rows.map((row) => row[column].length))) || [];
    const used = widths.map((width) => width > 0);

    return rows.map((row) =>
        row
            .map((cell, column) => (rightAligned.includes(column) ? cell.padStart(widths[column]) : cell.padEnd(widths[column])))
            .filter((_, column) => used[column])
            .join(COLUMN_GAP)
            .trimEnd()
    );
};

// cargoList: cargo lines of one or more shipments, each with its `containers` rows.
// sealFor(container) supplies a seal typed on the MBL / DBL record when the row has none.
// Returns { blocks: [{ lines: [{ text, desc, kgs, cbm, bold }], spaced }],
//           total: { lines: [{ text, kgs, cbm }], kgs, cbm } }:
// The TOTAL lines are aligned with the container lines: one line per container type under the
// type column ("20X40'HC", "5X20'DC" — 40HQ / 40'HC / 40 HC count as one type) and one line
// per package kind under the count / kind columns ("500 PALLETS", "100 BOXES"); weight and
// volume totals on the first line.
// one block per container; a multi-row container block is `spaced` (blank line around it).
export const buildBlCargoContent = (cargoList = [], { sealFor = () => "" } = {}) => {
    const cargos = (Array.isArray(cargoList) ? cargoList : []).filter(Boolean);

    // Cargo rows grouped by container number, in the order the containers first appear.
    const containers = new Map();
    let unnamed = 0;
    cargos.forEach((cargo) => {
        const rows = (cargo.containers || []).filter(
            (row) => row && (row.containerNo || row.sealNumber || row.containerType)
        );
        // Rows saved before units / weights were entered per container: a single container
        // takes the cargo's own figures, more than one can not be split.
        const single = rows.length === 1;

        rows.forEach((row) => {
            const key = containerKey(row.containerNo) || `#${(unnamed += 1)}`;
            if (!containers.has(key)) {
                containers.set(key, { containerNo: clean(row.containerNo), seal: "", type: "", items: [] });
            }
            const entry = containers.get(key);
            entry.seal = entry.seal || clean(row.sealNumber || sealFor(row));
            entry.type = entry.type || clean(row.containerType || cargo.containerType);
            entry.items.push({
                units: toNumber(row.unitQuantity) || (single ? toNumber(cargo.unitQuantity) : 0),
                packing: clean(row.packingType || cargo.selectedPackingType).toUpperCase(),
                description: clean(row.description || cargo.description || cargo.commodity),
                hsCode: clean(cargo.hsCode),
                grossWeight: toNumber(row.grossWeight) || (single ? toNumber(cargo.grossWeight) : 0),
                cbm: toNumber(row.cbm) || (single ? toNumber(cargo.cbm) : 0),
            });
        });
    });

    const list = [...containers.values()];
    const sum = (items, key) => items.reduce((total, item) => total + item[key], 0);
    const unitsText = (item) => (item.units ? `${item.units} ${item.packing}`.trim() : "");

    // Single-row containers: one aligned line each.
    const singles = list.filter((entry) => entry.items.length === 1);
    const allItems = list.flatMap((entry) => entry.items);

    // Totals, in the same columns as the container lines.
    // Container types: the same type written differently (40HQ, 40'HC, 40 HC) is one type.
    const typeKey = (type) =>
        clean(type).toUpperCase().replace(/[^A-Z0-9]/g, "").replace(/HQ/g, "HC");
    const typeTotals = new Map(); // key -> { label, count }
    list.forEach((entry) => {
        const key = typeKey(entry.type);
        if (!typeTotals.has(key)) typeTotals.set(key, { label: entry.type, count: 0 });
        const total = typeTotals.get(key);
        total.count += 1;
        // The form's own spelling (40'HC, 20'DC) wins over hand-typed ones (40HQ, 40 HC).
        if (entry.type.includes("'") && !total.label.includes("'")) total.label = entry.type;
    });
    const typeLines = [...typeTotals.values()].map(({ label, count }) =>
        label ? `${count}X${label}` : `${count} ${count === 1 ? "CONTAINER" : "CONTAINERS"}`
    );

    // Package kinds: PALLET / PALLETS count as one kind.
    const kindKey = (kind) => clean(kind).toUpperCase().replace(/[^A-Z0-9]/g, "").replace(/S$/, "");
    const kindTotals = new Map(); // key -> { label, units }
    allItems.forEach((item) => {
        if (!item.units) return;
        const key = kindKey(item.packing);
        if (!kindTotals.has(key)) kindTotals.set(key, { label: item.packing || "PACKAGES", units: 0 });
        kindTotals.get(key).units += item.units;
    });
    if (!kindTotals.size) {
        const cargoUnits = cargos.reduce((t, c) => t + toNumber(c.unitQuantity), 0);
        if (cargoUnits) kindTotals.set("", { label: "PACKAGES", units: cargoUnits });
    }
    const kindLines = [...kindTotals.values()];

    const totalCount = Math.max(1, typeLines.length, kindLines.length);
    const totalCells = Array.from({ length: totalCount }, (_, i) => [
        i === 0 ? "TOTAL" : "",
        "",
        typeLines[i] || "",
        kindLines[i] ? String(kindLines[i].units) : "",
        kindLines[i] ? kindLines[i].label : "",
    ]);

    const alignedRows = alignColumns(
        [
            ...singles.map((entry) => {
                const [item] = entry.items;
                return [entry.containerNo, entry.seal, entry.type, item.units ? String(item.units) : "", item.units ? item.packing : ""];
            }),
            ...totalCells,
        ],
        [3]
    );
    const totalTexts = alignedRows.splice(alignedRows.length - totalCount, totalCount);
    const singleText = new Map(singles.map((entry, index) => [entry, alignedRows[index]]));

    // Width of the stacked number / type / seal column of multi-row containers.
    const identWidth = Math.max(
        0,
        ...list.filter((entry) => entry.items.length > 1).flatMap((entry) => [entry.containerNo, entry.type, entry.seal].map((v) => v.length))
    );

    const blocks = [];

    list.forEach((entry) => {
        if (entry.items.length === 1) {
            const [item] = entry.items;
            blocks.push({
                lines: [{
                    text: singleText.get(entry),
                    desc: item.description,
                    kgs: item.grossWeight ? kgs(item.grossWeight) : "",
                    cbm: item.cbm ? cbms(item.cbm) : "",
                }],
                spaced: false,
            });
            return;
        }

        const totalKgs = sum(entry.items, "grossWeight");
        const totalCbm = sum(entry.items, "cbm");
        const pieces = sum(entry.items, "units");
        const unitTexts = entry.items.map(unitsText);
        // One kind (PALLET / PALLETS count as one) or, when they differ, PACKAGES.
        const blockKinds = new Set(entry.items.map((item) => clean(item.packing).toUpperCase().replace(/[^A-Z0-9]/g, "").replace(/S$/, "")));
        const totalUnitsText = pieces
            ? `${pieces} ${blockKinds.size === 1 ? entry.items[0].packing || "PACKAGES" : "PACKAGES"}`.trim()
            : "";
        const unitsWidth = Math.max(0, totalUnitsText.length, ...unitTexts.map((text) => text.length));
        const idents = [entry.containerNo, entry.type, entry.seal];
        const lineCount = Math.max(entry.items.length, idents.length);
        const lines = [];
        for (let i = 0; i < lineCount; i += 1) {
            const item = entry.items[i];
            lines.push({
                text: [(idents[i] || "").padEnd(identWidth), (unitTexts[i] || "").padStart(unitsWidth)].join(COLUMN_GAP).trimEnd(),
                desc: item ? [item.description, item.hsCode ? `GTIP:${item.hsCode}` : ""].filter(Boolean).join("- ") : "",
                kgs: i === 0 && totalKgs ? kgs(totalKgs) : "",
                cbm: i === 0 && totalCbm ? cbms(totalCbm) : "",
            });
        }
        lines.push({
            text: ["".padEnd(identWidth), totalUnitsText.padStart(unitsWidth)].join(COLUMN_GAP).trimEnd(),
            desc: "TOTAL OF CONTAINER",
            kgs: "",
            cbm: "",
        });
        blocks.push({ lines, spaced: true });
    });

    const totalKgs = sum(allItems, "grossWeight") || cargos.reduce((t, c) => t + toNumber(c.grossWeight), 0);
    const totalCbm = sum(allItems, "cbm") || cargos.reduce((t, c) => t + toNumber(c.cbm), 0);

    return {
        blocks,
        total: {
            lines: totalTexts.map((text, i) => ({
                text,
                kgs: i === 0 && totalKgs ? kgs(totalKgs) : "",
                cbm: i === 0 && totalCbm ? cbms(totalCbm) : "",
            })),
            kgs: totalKgs ? kgs(totalKgs) : "",
            cbm: totalCbm ? cbms(totalCbm) : "",
        },
    };
};

// Word-wraps text to maxWidth; over-long words are split by character.
const wrapLine = (text, font, size, maxWidth) => {
    if (!text) return [""];
    const fits = (value) => font.widthOfTextAtSize(value, size) <= maxWidth;
    if (fits(text)) return [text];
    const out = [];
    let line = "";
    text.split(/\s+/).filter(Boolean).forEach((word) => {
        const candidate = line ? `${line} ${word}` : word;
        if (fits(candidate)) {
            line = candidate;
            return;
        }
        if (line) out.push(line);
        let rest = word;
        while (!fits(rest) && rest.length > 1) {
            let cut = rest.length - 1;
            while (cut > 1 && !fits(rest.slice(0, cut))) cut -= 1;
            out.push(rest.slice(0, cut));
            rest = rest.slice(cut);
        }
        line = rest;
    });
    out.push(line);
    return out;
};

// Largest size up to `size` (not below `min`) at which every text fits in maxWidth.
const fitSize = (texts, font, size, maxWidth, min = 5) => {
    let fitted = size;
    const widest = () => Math.max(0, ...texts.map((text) => font.widthOfTextAtSize(text, fitted)));
    while (fitted > min && widest() > maxWidth) fitted -= 0.25;
    return fitted;
};

// Route cells (place of receipt, vessel, port of loading / discharge, place of delivery) are
// one line each and printed at one common size, so a long port name does not end up smaller
// than the others. A value too long for its cell at the normal size is shortened first, the
// way carriers write it: its UN/LOCODE "(XXXXX)" goes, then the alternative names after " / "
// ("PECEM PT / SAO GONCALO DO AMARANTE, BRAZIL" -> "PECEM PT, BRAZIL"). Only if that is not
// enough do all cells shrink together (not below `minSize`), and as a last resort the value is
// cut with "...".
// entries: [{ key, text, maxWidth }] -> { size, texts: { key: text } }
const SHORTENERS = [
    (text) => text.replace(/\s*\([A-Z0-9]{5}\)/g, ""),
    (text) => text.replace(/\s*\/[^,]*(?=,|$)/, ""),
];

export const fitRouteFields = (entries, font, { size = 7, minSize = 6 } = {}) => {
    const width = (text, s) => font.widthOfTextAtSize(text, s);
    const list = entries.filter((entry) => entry.text);
    const texts = Object.fromEntries(list.map((entry) => [entry.key, entry.text]));
    const fitsAll = (s) => list.every((entry) => width(texts[entry.key], s) <= entry.maxWidth);

    list.forEach((entry) => {
        SHORTENERS.forEach((shorten) => {
            if (width(texts[entry.key], size) > entry.maxWidth) texts[entry.key] = shorten(texts[entry.key]).trim();
        });
    });

    let fitted = minSize;
    for (let s = size; s >= minSize; s -= 0.25) {
        if (fitsAll(s)) {
            fitted = s;
            break;
        }
    }

    list.forEach((entry) => {
        let text = texts[entry.key];
        if (width(text, fitted) <= entry.maxWidth) return;
        while (text.length > 1 && width(`${text}...`, fitted) > entry.maxWidth) text = text.slice(0, -1);
        texts[entry.key] = `${text.trimEnd()}...`;
    });

    return { size: fitted, texts };
};

// Draws text inside a cell: `top` is where the first line's capitals start (just under the
// printed label), `bottom` the lowest point the text may reach (just above the ruling line).
// Lines wrap at maxWidth and the font shrinks until the whole value fits the cell, so a party
// block with address, contact and tax lines never runs over the next box.
export const drawFittedText = (page, text, cell, font, normalize = (v) => v) => {
    if (text === null || text === undefined || text === "") return;
    const value = normalize(String(text));
    const available = cell.top - cell.bottom;
    const height = (count, s) => (count - 1) * s * 1.15 + s * 0.93;
    let size = cell.size || 9;
    let lines = value.split("\n").flatMap((part) => wrapLine(part, font, size, cell.maxWidth));
    while (size > 4 && height(lines.length, size) > available) {
        size -= 0.25;
        lines = value.split("\n").flatMap((part) => wrapLine(part, font, size, cell.maxWidth));
    }
    lines.forEach((line, index) => {
        if (!line) return;
        page.drawText(line, {
            x: cell.x,
            y: cell.top - size * 0.72 - index * size * 1.15,
            size,
            font,
            color: cell.color || rgb(0, 0, 0),
        });
    });
};

// Party boxes of the Filenders MBL / DBL form (ocean-mbl-seawaybill.pdf), between the printed
// labels and the ruling lines.
export const FILENDERS_MBL_PARTY_CELLS = {
    consignor: { x: 70, top: 785, bottom: 731, size: 9, maxWidth: 220 },
    consignee: { x: 70, top: 711, bottom: 666, size: 9, maxWidth: 220 },
    notify: { x: 70, top: 643, bottom: 599, size: 9, maxWidth: 220 },
};

const ROUTE_KEYS = ["placeOfReceipt", "vesselName", "pol", "pod", "placeOfDelivery"];

// Draws the route cells of a B/L page with fitRouteFields. A cell layout is either
// { x, top, maxWidth } (text starts under the label at `top`) or { x, y, maxWidth } (baseline).
export const drawRouteFields = ({ page, font, layout, fieldData, normalize = (v) => v, size = 7 }) => {
    const entries = ROUTE_KEYS.filter((key) => layout[key])
        .map((key) => ({ key, text: normalize(String(fieldData[key] || "")).trim(), maxWidth: layout[key].maxWidth }));
    const fitted = fitRouteFields(entries, font, { size });

    entries.forEach(({ key }) => {
        const text = fitted.texts[key];
        if (!text) return;
        const cell = layout[key];
        page.drawText(text, {
            x: cell.x,
            y: cell.top !== undefined ? cell.top - fitted.size * 0.72 : cell.y,
            size: fitted.size,
            font,
            color: cell.color || rgb(0, 0, 0),
        });
    });
};

// Area for drawBlCargo from a page's column layout (`layout.cargo` of the print pages):
// container text runs from the Marks and numbers column up to the Gross weight column.
// overrides may set x / startY / minY, the column starts weightX / measurementX, or
// measurementRight (where the cargo box ends on the right).
//
// The Filenders MBL / DBL form (ocean-mbl-seawaybill.pdf) has its captions at x 27
// (Marks and numbers), 425 (Gross weight) and 510 (Measurement), its cargo box ends at
// x 566 and above "according to the declaration of the consignor" (~y 307).
export const FILENDERS_MBL_CARGO_AREA = { x: 27, descriptionX: 305, weightX: 425, measurementX: 510, measurementRight: 566, minY: 315 };
export const blCargoArea = (cargo, overrides = {}) => {
    const offset = cargo.xOffset || 0;
    const {
        x = cargo.marksAndNumbers.x + offset,
        descriptionX = cargo.description ? cargo.description.x + offset : undefined,
        weightX = cargo.grossWeight.x + offset,
        measurementX = cargo.measurement.x + offset,
        ...rest
    } = overrides;

    // Without a description column the goods follow the packages on the same line.
    const descX = descriptionX && descriptionX > x && descriptionX < weightX ? descriptionX : null;

    return {
        x,
        maxWidth: (descX || weightX) - x - 8,
        descriptionX: descX,
        descriptionWidth: descX ? weightX - descX - 8 : 0,
        startY: cargo.startY,
        minY: cargo.minY,
        size: 8,
        lineHeight: 10,
        weightX,
        measurementX,
        ...rest,
    };
};

const CONTINUED = "*** CONTINUED ON ATTACHED SHEET ***";

// "ORIGINAL" printed in white on the blue FBL form (original-front.pdf), measured on the scan:
// x 218.7–396.9, capitals from y 283.6 to 311.5. A copy covers it with the paper tint and
// prints "COPY" in its place, the same size and colour.
const FBL_ORIGINAL_MARK = { x: 213, y: 279, width: 190, height: 37, centerX: 307.8, baseline: 283.6, size: 39 };
export const FBL_FRONT_TINT = rgb(226 / 255, 252 / 255, 253 / 255);
export const FBL_SHEET_TINT = rgb(222 / 255, 250 / 255, 252 / 255);

export const drawCopyMark = async (pdfDoc, page, tint = FBL_FRONT_TINT) => {
    const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const mark = FBL_ORIGINAL_MARK;
    page.drawRectangle({ x: mark.x, y: mark.y, width: mark.width, height: mark.height, color: tint, borderWidth: 0 });
    const text = "COPY";
    page.drawText(text, {
        x: mark.centerX - bold.widthOfTextAtSize(text, mark.size) / 2,
        y: mark.baseline,
        size: mark.size,
        font: bold,
        color: rgb(1, 1, 1),
    });
};

// Background of the attached sheets: the form's paper colour, frame and watermark without its
// tables and captions, kept next to the form as "<form>-sheet.jpg" (public/documents/cmr).
// null when there is none, and the sheets are then plain A4.
export const loadSheetBackground = async (formUrl) => {
    if (typeof formUrl !== "string" || !/.pdf$/i.test(formUrl)) return null;
    try {
        const response = await fetch(formUrl.replace(/.pdf$/i, "-sheet.jpg"));
        return response.ok ? await response.arrayBuffer() : null;
    } catch {
        return null;
    }
};
const A4 = [595.28, 841.89];
const BLACK = rgb(0, 0, 0);
const GREY = rgb(0.35, 0.35, 0.35);

// area: { x, maxWidth, startY, minY, size, lineHeight, weightX, measurementX } in
// front-page points (see blCargoArea).
// normalize: the page's text normaliser (Turkish letters -> ASCII, upper case).
// background: JPG bytes of the sheet background (loadSheetBackground); attached sheets are then
// printed on it, the size of the front page.
// markSheet(page): draws over the background of each attached sheet (the COPY mark).
export const drawBlCargo = async ({
    pdfDoc,
    page,
    area,
    content,
    blNumber = "",
    normalize = (v) => v,
    background = null,
    markSheet = null,
}) => {
    const mono = await pdfDoc.embedFont(StandardFonts.Courier);
    const monoBold = await pdfDoc.embedFont(StandardFonts.CourierBold);
    const lineHeight = area.lineHeight || 10;

    const blocks = (content.blocks || []).map((block) => ({
        ...block,
        lines: block.lines.map((line) => ({ ...line, text: normalize(line.text || "") })),
    }));
    // Without a description column the goods are appended to the line.
    if (!area.descriptionX) {
        blocks.forEach((block) => block.lines.forEach((line) => {
            line.text = [line.text, line.desc].filter(Boolean).join(COLUMN_GAP);
            line.desc = "";
        }));
    }
    blocks.forEach((block) => block.lines.forEach((line) => { line.desc = normalize(line.desc || ""); }));
    const allTexts = blocks.flatMap((block) => block.lines.map((line) => line.text));
    // A blank line goes between two blocks when either of them is a multi-row container.
    const gapBefore = (i) => (i > 0 && (blocks[i].spaced || blocks[i - 1].spaced) ? 1 : 0);

    // One size for the whole block, so the columns stay aligned; it shrinks (down to 6) for
    // long descriptions before anything wraps.
    const size = fitSize(allTexts, mono, area.size || 8, area.maxWidth, 6);
    // (descriptions wrap in their column instead of shrinking the whole block)

    // A "page" for drawing: where text starts, its width and the right edges of the two
    // figure columns (weight ends before Measurement, measurement at the box edge).
    const front = {
        page,
        x: area.x,
        maxWidth: area.maxWidth,
        descX: area.descriptionX,
        descWidth: area.descriptionWidth,
        weightLeft: area.weightX,
        weightRight: area.measurementX - 6,
        cbmLeft: area.measurementX,
        cbmRight: area.measurementRight || page.getWidth() - 20,
    };

    const drawRow = (target, row, y) => {
        const rowSize = target.size || size;
        if (row.text) {
            target.page.drawText(row.text, { x: target.x, y, size: rowSize, font: row.bold ? monoBold : mono, color: BLACK });
        }
        if (row.desc && target.descX) {
            target.page.drawText(row.desc, { x: target.descX, y, size: rowSize, font: mono, color: BLACK });
        }
        [
            [row.kgs, target.weightLeft, target.weightRight],
            [row.cbm, target.cbmLeft, target.cbmRight],
        ].forEach(([value, left, right]) => {
            if (!value) return;
            const text = normalize(value);
            const font = row.bold ? monoBold : mono;
            const s = fitSize([text], font, rowSize, right - left);
            target.page.drawText(text, { x: right - font.widthOfTextAtSize(text, s), y, size: s, font, color: BLACK });
        });
    };

    // Rows of a block: a too long text or description wraps onto extra rows; the figures stay
    // on the first one.
    const blockRows = (block, target) =>
        block.lines.flatMap((line) => {
            const rowSize = target.size || size;
            const texts = wrapLine(line.text, mono, rowSize, target.maxWidth);
            const descs = line.desc && target.descX
                ? wrapLine(line.desc, mono, rowSize, target.descWidth).map((part) => part.replace(/s*-$/, ""))
                : [];
            return Array.from({ length: Math.max(texts.length, descs.length, 1) }, (_, index) => ({
                ...line,
                text: texts[index] || "",
                desc: descs[index] || "",
                kgs: index === 0 ? line.kgs : "",
                cbm: index === 0 ? line.cbm : "",
            }));
        });

    // TOTAL  25 CONTAINERS  600 PACKAGES  ...  678230.000 KGS  2150.0000 CBM
    // TOTAL      25X40'HC  600 PACKAGES  ...  678230.000 KGS  2150.0000 CBM
    const totalRows = (content.total?.lines || []).map((line) => ({ ...line, text: normalize(line.text), bold: true }));

    // Front page: whole blocks while they fit, then the continuation note.
    const capacity = Math.max(1, Math.floor((area.startY - area.minY) / lineHeight) + 1);
    let y = area.startY;
    let used = 0;
    let index = 0;
    const blank = (count) => {
        y -= lineHeight * count;
        used += count;
    };

    const allRowsCount =
        blocks.reduce((n, block, i) => n + blockRows(block, front).length + gapBefore(i), 0) + (totalRows.length ? totalRows.length + 1 : 0);
    const fitsOnFront = allRowsCount <= capacity;

    for (; index < blocks.length; index += 1) {
        const rows = blockRows(blocks[index], front);
        const needed = rows.length + gapBefore(index) + (fitsOnFront ? 0 : 1); // keep a line for the note
        if (used + needed > capacity) break;
        if (gapBefore(index)) blank(1);
        rows.forEach((row) => {
            drawRow(front, row, y);
            blank(1);
        });
    }

    if (index === blocks.length) {
        if (totalRows.length) {
            blank(1);
            totalRows.forEach((row) => {
                drawRow(front, row, y);
                blank(1);
            });
        }
        return;
    }

    drawRow(front, { text: CONTINUED, bold: true }, y);

    // Attached sheets in the form's style: its background only (no tables), the cargo at the
    // same columns as on the front page, from the top of the page down: "PAGE: 2 / 3" on the first
    // line, the list right under it (no B/L number on the sheet).
    if (background) {
        const image = await pdfDoc.embedJpg(background);
        const form = { width: page.getWidth(), height: page.getHeight() };
        const sheetTop = form.height - 50;
        const listTop = sheetTop - lineHeight * 2;
        const sheetCapacityOnForm = Math.max(2, Math.floor((listTop - 45) / lineHeight) + 1);
        const formSheets = [[]];
        for (; index < blocks.length; index += 1) {
            const rows = blockRows(blocks[index], front);
            let current = formSheets[formSheets.length - 1];
            const gap = current.length ? gapBefore(index) : 0;
            if (current.length && current.length + gap + rows.length > sheetCapacityOnForm) {
                current = [];
                formSheets.push(current);
            }
            if (current.length && gap) current.push(null);
            current.push(...rows);
        }
        if (totalRows.length) {
            let current = formSheets[formSheets.length - 1];
            if (current.length + totalRows.length + 1 > sheetCapacityOnForm) {
                current = [];
                formSheets.push(current);
            }
            current.push(null, ...totalRows);
        }

        for (const [sheetIndex, rows] of formSheets.entries()) {
            const sheetPage = pdfDoc.addPage([form.width, form.height]);
            sheetPage.drawImage(image, { x: 0, y: 0, width: form.width, height: form.height });
            if (markSheet) await markSheet(sheetPage);
            const target = { ...front, page: sheetPage };
            rows.forEach((row, rowIndex) => {
                if (row) drawRow(target, row, listTop - rowIndex * lineHeight);
            });
            const pageText = `PAGE: ${sheetIndex + 2} / ${formSheets.length + 1}`;
            drawRow(target, { text: pageText, bold: true }, sheetTop);
        }
        return;
    }

    // Attached sheets: plain A4 pages framed like the cargo box, with the B/L number and the
    // page number on top, like the carrier's own.
    const sans = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const margin = 42;
    const boxTop = A4[1] - 64;
    const sheetX = margin + 10;
    const sheetCbmRight = A4[0] - margin - 8;
    const sheetCbmLeft = sheetCbmRight - 70;
    const sheetWeightRight = sheetCbmLeft - 6;
    const sheetWeightLeft = sheetWeightRight - 80;
    const sheetCapacity = Math.floor((boxTop - margin - 28) / lineHeight);
    // Same proportions as the front page: the description column starts at the same share
    // of the width between the text start and the weight column.
    // The sheet is narrower than the cargo box: its text size is chosen so the container
    // columns stay on one line (wrapping would lose their spacing) while the description column
    // keeps at least 130 pt; the description starts right after the widest container text.
    const sheetSize = area.descriptionX
        ? fitSize(allTexts, mono, size, sheetWeightLeft - sheetX - 130 - 12, 5)
        : size;
    const widestText = Math.max(0, ...allTexts.map((text) => mono.widthOfTextAtSize(text, sheetSize)));
    const sheetDescX = area.descriptionX ? sheetX + widestText + 12 : null;
    const sheetLayout = {
        size: sheetSize,
        maxWidth: (sheetDescX || sheetWeightLeft) - sheetX - 8,
        descX: sheetDescX,
        descWidth: sheetDescX ? sheetWeightLeft - sheetDescX - 8 : 0,
    };

    const sheets = [];
    let current = null;
    const newSheet = () => {
        current = [];
        sheets.push(current);
    };
    newSheet();
    for (; index < blocks.length; index += 1) {
        const rows = blockRows(blocks[index], sheetLayout);
        const gap = current.length ? gapBefore(index) : 0;
        if (current.length && current.length + gap + rows.length > sheetCapacity) newSheet();
        if (current.length && gap) current.push(null);
        current.push(...rows);
    }
    if (totalRows.length) {
        if (current.length + totalRows.length + 1 > sheetCapacity) newSheet();
        current.push(null, ...totalRows);
    }

    sheets.forEach((rows, sheetIndex) => {
        const sheetPage = pdfDoc.addPage(A4);
        const blText = normalize(`B/L: ${blNumber || "-"}`);
        const pageText = `PAGE: ${sheetIndex + 2} / ${sheets.length + 1}`;
        sheetPage.drawText(blText, { x: margin, y: A4[1] - 48, size: 9, font: sans, color: GREY });
        sheetPage.drawText(pageText, {
            x: A4[0] - margin - sans.widthOfTextAtSize(pageText, 9),
            y: A4[1] - 48,
            size: 9,
            font: sans,
            color: GREY,
        });
        sheetPage.drawRectangle({
            x: margin,
            y: margin,
            width: A4[0] - margin * 2,
            height: boxTop - margin,
            borderColor: BLACK,
            borderWidth: 0.8,
        });
        sheetPage.drawText("CONTAINER DETAILS (CONTINUED)", { x: margin + 10, y: boxTop - 14, size: 7, font: sans, color: GREY });

        const target = {
            page: sheetPage,
            x: sheetX,
            size: sheetLayout.size,
            descX: sheetLayout.descX,
            weightLeft: sheetWeightLeft,
            weightRight: sheetWeightRight,
            cbmLeft: sheetCbmLeft,
            cbmRight: sheetCbmRight,
        };
        rows.forEach((row, rowIndex) => {
            if (row) drawRow(target, row, boxTop - 32 - rowIndex * lineHeight);
        });
    });
};
